import { useMemo } from "react";
import type { Game } from "@/domain/types";
import { type ActiveSetup, resolveActiveSetup, UPLOADED_ID } from "@/platform/catalog/active-setup";

/**
 * The page-side face of the ActiveSetup seam: memoizes the one impure-ish step (re-ingesting a
 * simple Game's persisted upload text through the Game's own parser, so an unrelated render never
 * re-parses), then resolves the active setup for this side. SC2 rides its Source model, not this
 * ingest - its upload is already a source text.
 */
export function useActiveSetup({
  game,
  sc2SourceId,
  sc2Upload,
  selectedProfileId,
  simpleUpload,
}: {
  game: Game;
  /** The persisted SC2 source selection. */
  sc2SourceId: string;
  /** The session's uploaded .SC2Hotkeys, offered as its own source. */
  sc2Upload: { text: string; name: string } | null;
  /** The persisted simple-Game Profile selection for this Game. */
  selectedProfileId: string | undefined;
  /** A simple Game's persisted "my own" upload (raw export text), re-ingested here. */
  simpleUpload: { text: string; name: string } | null;
}): ActiveSetup {
  const uploadedProfile = useMemo(
    () =>
      game.id !== "sc2" && simpleUpload && game.ingestUpload
        ? game.ingestUpload(simpleUpload.text, { id: UPLOADED_ID, name: simpleUpload.name })
        : null,
    [game, simpleUpload],
  );
  return resolveActiveSetup({ game, sc2SourceId, sc2Upload, selectedProfileId, uploadedProfile });
}
